import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Search } from "lucide-react";

interface Item {
  id: string;
  name: string;
  email?: string;
  profile?: {
    fullName?: string;
  };
  [key: string]: any;
}

interface MultiSelectTableProps {
  title: string;
  items: Item[];
  selectedIds: string[];
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
  searchPlaceholder?: string;
  emptyMessage?: string;
  displayField?: (item: Item) => string;
}

export const MultiSelectTable = ({
  title,
  items,
  selectedIds,
  onAdd,
  onRemove,
  searchPlaceholder = "Pesquisar...",
  emptyMessage = "Nenhum item selecionado",
  displayField = (item) => item.name,
}: MultiSelectTableProps) => {
  const [search, setSearch] = useState("");

  const availableItems = items?.filter(
    (item) =>
      !selectedIds.includes(item.id) &&
      (displayField(item)?.toLowerCase()?.includes(search.toLowerCase()) ||
        item.email?.toLowerCase()?.includes(search.toLowerCase())),
  );

  const selectedItems = items?.filter((item) => selectedIds.includes(item.id));

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium">{title}</label>

        {/* Search and Add Section */}
        <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          {search && availableItems && availableItems.length > 0 && (
            <div className="max-h-48 overflow-y-auto space-y-1 border rounded-md bg-white p-2">
              {availableItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-2 hover:bg-gray-100 rounded-md cursor-pointer transition-colors"
                  onClick={() => {
                    onAdd(item.id);
                    setSearch("");
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-medium truncate">
                      {displayField(item)}
                    </p>
                    {item.email && displayField(item).includes("@") && (
                      <p className="text-xs text-gray-500 truncate">
                        {item.email}
                      </p>
                    )}
                  </div>
                  <Plus className="h-4 w-4 text-gray-400" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Selected Items Table */}
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead className="w-20 text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {selectedItems && selectedItems.length > 0 ? (
                selectedItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div>
                        <p className="text-base font-medium">
                          {displayField(item)}
                        </p>
                        {item.email && displayField(item).includes("@") && (
                          <p className="text-xs text-gray-500">{item.email}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemove(item.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} className="text-center text-gray-500">
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};
